// Hand-drawn SVG illustrations for common rental-maintenance fixes.
// Ship as inline components so they load instantly on the tenant portal
// (no external image host, no LLM cost per view, offline-friendly).
// Each illustration is keyed off a string that Claude picks in the triage
// system prompt: gfci_reset, disposal_reset, disposal_wrench, shutoff_valve,
// thermostat_batteries, breaker_panel, smoke_alarm_battery.

import { Warning } from "@phosphor-icons/react";

const SIZE = { width: 220, height: 160 };

function Card({ children, label, caption }) {
  return (
    <div className="mt-2 bg-white border-2 border-cyan-500 shadow-[3px_3px_0px_#0f172a] max-w-xs"
      data-testid={`illustration-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
      <div className="bg-cyan-500 text-white px-2 py-1 text-[9px] font-black uppercase tracking-widest">
        🔍 Look for this: {label}
      </div>
      <div className="p-2 bg-slate-50 flex items-center justify-center">
        {children}
      </div>
      {caption && (
        <div className="px-2 py-1.5 text-[10px] font-medium text-slate-700 border-t border-slate-200">
          {caption}
        </div>
      )}
    </div>
  );
}

// ── GFCI Reset Button (kitchen/bath outlet with red RESET + black TEST) ──
export function GFCIReset() {
  return (
    <Card label="GFCI Reset Button" caption="A regular-looking outlet with two small buttons in the middle — the top RED one says RESET. Press it firmly until it clicks.">
      <svg viewBox="0 0 220 160" width={SIZE.width} height={SIZE.height}>
        <rect x="70" y="15" width="80" height="130" rx="6" fill="#f8fafc" stroke="#0f172a" strokeWidth="2.5"/>
        <circle cx="110" cy="30" r="2" fill="#0f172a"/>
        {/* RESET button (red, top) */}
        <rect x="95" y="45" width="30" height="14" rx="3" fill="#dc2626" stroke="#0f172a" strokeWidth="1.5"/>
        <text x="110" y="55" textAnchor="middle" fontSize="7" fontWeight="900" fill="white" fontFamily="monospace">RESET</text>
        {/* TEST button (black, below) */}
        <rect x="95" y="66" width="30" height="14" rx="3" fill="#1e293b" stroke="#0f172a" strokeWidth="1.5"/>
        <text x="110" y="76" textAnchor="middle" fontSize="7" fontWeight="900" fill="white" fontFamily="monospace">TEST</text>
        {/* Outlet slots (bottom) */}
        <rect x="90" y="100" width="4" height="14" fill="#0f172a"/>
        <rect x="102" y="100" width="4" height="14" fill="#0f172a"/>
        <circle cx="118" cy="118" r="3" fill="#0f172a"/>
        <rect x="126" y="100" width="4" height="14" fill="#0f172a"/>
        <rect x="138" y="100" width="4" height="14" fill="#0f172a"/>
        {/* Arrow pointing at the RESET button */}
        <path d="M 35 52 L 88 52 M 82 47 L 88 52 L 82 57" stroke="#dc2626" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
        <text x="15" y="45" fontSize="9" fontWeight="900" fill="#dc2626" fontFamily="monospace">PRESS</text>
        <text x="15" y="60" fontSize="9" fontWeight="900" fill="#dc2626" fontFamily="monospace">THIS</text>
      </svg>
    </Card>
  );
}

// ── Disposal Reset Button (bottom of cylinder, small red button) ──
export function DisposalReset() {
  return (
    <Card label="Disposal Reset Button" caption="Look under the sink — the disposal is the round metal cylinder. On the very BOTTOM of it is a small red or orange button. That's the reset.">
      <svg viewBox="0 0 220 160" width={SIZE.width} height={SIZE.height}>
        {/* Sink base pipe */}
        <rect x="90" y="10" width="40" height="15" fill="#94a3b8" stroke="#0f172a" strokeWidth="1.5"/>
        {/* Disposal cylinder (rotated view showing bottom) */}
        <ellipse cx="110" cy="30" rx="45" ry="10" fill="#334155" stroke="#0f172a" strokeWidth="2"/>
        <rect x="65" y="30" width="90" height="80" fill="#475569" stroke="#0f172a" strokeWidth="2"/>
        {/* Bottom curved edge */}
        <ellipse cx="110" cy="110" rx="45" ry="10" fill="#1e293b" stroke="#0f172a" strokeWidth="2"/>
        {/* Reset button on bottom - small red */}
        <circle cx="110" cy="113" r="6" fill="#dc2626" stroke="#0f172a" strokeWidth="2"/>
        <circle cx="110" cy="113" r="2" fill="#7f1d1d"/>
        {/* Arrow */}
        <path d="M 165 130 L 118 118 M 122 111 L 118 118 L 125 121" stroke="#dc2626" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
        <text x="170" y="140" fontSize="9" fontWeight="900" fill="#dc2626" fontFamily="monospace">RESET</text>
        <text x="170" y="152" fontSize="9" fontWeight="900" fill="#dc2626" fontFamily="monospace">BUTTON</text>
        {/* Warning */}
        <text x="20" y="150" fontSize="7" fontWeight="700" fill="#0f172a" fontFamily="monospace">⚠ Never put</text>
        <text x="20" y="158" fontSize="7" fontWeight="700" fill="#0f172a" fontFamily="monospace">hand inside!</text>
      </svg>
    </Card>
  );
}

// ── Jambuster / Allen Wrench ──
export function DisposalWrench() {
  return (
    <Card label="Jambuster / Allen Wrench" caption="A small red L-shaped hex key, usually stashed under the sink. Slides into a hole on the bottom of the disposal to rotate the blades manually.">
      <svg viewBox="0 0 220 160" width={SIZE.width} height={SIZE.height}>
        {/* L-shaped Allen key */}
        <path d="M 40 40 L 40 110 L 160 110" stroke="#dc2626" strokeWidth="18" fill="none" strokeLinecap="square" strokeLinejoin="miter"/>
        {/* Hex tip highlight */}
        <polygon points="160,105 175,105 178,110 175,115 160,115" fill="#7f1d1d" stroke="#0f172a" strokeWidth="1.5"/>
        {/* Inner shine */}
        <path d="M 44 44 L 44 106 L 156 106" stroke="#f87171" strokeWidth="2" fill="none" strokeLinecap="round"/>
        {/* Label */}
        <text x="55" y="30" fontSize="10" fontWeight="900" fill="#0f172a" fontFamily="monospace">JAMBUSTER</text>
        <text x="70" y="145" fontSize="9" fontWeight="700" fill="#64748b" fontFamily="monospace">Hex-shaped tip →</text>
      </svg>
    </Card>
  );
}

// ── Under-Sink Shutoff Valve ──
export function ShutoffValve() {
  return (
    <Card label="Under-Sink Shutoff Valve" caption="Two small oval knobs on the water pipes under the sink (one hot, one cold). Turn them CLOCKWISE (righty-tighty) to stop the water.">
      <svg viewBox="0 0 220 160" width={SIZE.width} height={SIZE.height}>
        {/* Wall */}
        <rect x="0" y="120" width="220" height="40" fill="#e2e8f0"/>
        {/* Copper pipes */}
        <rect x="60" y="60" width="8" height="70" fill="#f97316" stroke="#0f172a" strokeWidth="1.5"/>
        <rect x="152" y="60" width="8" height="70" fill="#3b82f6" stroke="#0f172a" strokeWidth="1.5"/>
        {/* Valve bodies */}
        <rect x="52" y="70" width="24" height="30" rx="2" fill="#a3a3a3" stroke="#0f172a" strokeWidth="2"/>
        <rect x="144" y="70" width="24" height="30" rx="2" fill="#a3a3a3" stroke="#0f172a" strokeWidth="2"/>
        {/* Oval handles */}
        <ellipse cx="64" cy="55" rx="12" ry="6" fill="#dc2626" stroke="#0f172a" strokeWidth="2"/>
        <ellipse cx="156" cy="55" rx="12" ry="6" fill="#3b82f6" stroke="#0f172a" strokeWidth="2"/>
        {/* Labels */}
        <text x="46" y="30" fontSize="9" fontWeight="900" fill="#dc2626" fontFamily="monospace">HOT</text>
        <text x="138" y="30" fontSize="9" fontWeight="900" fill="#1e40af" fontFamily="monospace">COLD</text>
        {/* Rotation arrows */}
        <path d="M 76 55 A 8 8 0 1 1 76 54.5" stroke="#0f172a" strokeWidth="2" fill="none" markerEnd="url(#ah)"/>
        <defs>
          <marker id="ah" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto">
            <path d="M 0 0 L 8 5 L 0 10 z" fill="#0f172a"/>
          </marker>
        </defs>
        <text x="55" y="145" fontSize="8" fontWeight="700" fill="#0f172a" fontFamily="monospace">Turn CLOCKWISE →</text>
      </svg>
    </Card>
  );
}

// ── Thermostat Batteries ──
export function ThermostatBatteries() {
  return (
    <Card label="Thermostat Batteries" caption="Slide the thermostat cover UP or off the wall. You'll see two AA batteries inside. Replace them if the screen is blank or dim.">
      <svg viewBox="0 0 220 160" width={SIZE.width} height={SIZE.height}>
        {/* Wall */}
        <rect x="20" y="15" width="180" height="130" rx="4" fill="#f8fafc" stroke="#0f172a" strokeWidth="2"/>
        {/* Screen area */}
        <rect x="35" y="30" width="150" height="55" rx="2" fill="#334155" stroke="#0f172a" strokeWidth="1.5"/>
        <text x="110" y="60" textAnchor="middle" fontSize="20" fontWeight="900" fill="#10b981" fontFamily="monospace">72°</text>
        {/* Battery compartment (open) */}
        <rect x="45" y="95" width="130" height="40" rx="2" fill="#e2e8f0" stroke="#0f172a" strokeWidth="1.5"/>
        {/* Battery 1 */}
        <rect x="55" y="105" width="55" height="20" rx="2" fill="#fde68a" stroke="#0f172a" strokeWidth="1.5"/>
        <rect x="107" y="110" width="4" height="10" fill="#0f172a"/>
        <text x="60" y="119" fontSize="8" fontWeight="900" fill="#0f172a" fontFamily="monospace">AA +</text>
        {/* Battery 2 */}
        <rect x="115" y="105" width="55" height="20" rx="2" fill="#fde68a" stroke="#0f172a" strokeWidth="1.5"/>
        <rect x="112" y="110" width="4" height="10" fill="#0f172a"/>
        <text x="121" y="119" fontSize="8" fontWeight="900" fill="#0f172a" fontFamily="monospace">- AA</text>
      </svg>
    </Card>
  );
}

// ── Breaker Panel ──
export function BreakerPanel() {
  return (
    <Card label="Breaker Panel" caption="Usually a gray metal box in the garage, basement, or hallway closet. Open it and look for any switch flipped to the middle or OFF — flip it fully OFF, then back ON.">
      <svg viewBox="0 0 220 160" width={SIZE.width} height={SIZE.height}>
        {/* Panel outer */}
        <rect x="30" y="10" width="160" height="140" rx="2" fill="#94a3b8" stroke="#0f172a" strokeWidth="2"/>
        {/* Rows of breakers */}
        {[0,1,2,3,4].map(row => (
          <g key={row}>
            {/* Left column */}
            <rect x={45} y={22 + row * 24} width="55" height="18" rx="1" fill="#1e293b" stroke="#0f172a" strokeWidth="1"/>
            <rect x={72 + (row === 2 ? 0 : (row%2===0? -8 : 8))} y={26 + row * 24} width="12" height="10" rx="1"
                  fill={row === 2 ? "#dc2626" : "#f8fafc"} stroke="#0f172a" strokeWidth="1"/>
            {/* Right column */}
            <rect x={120} y={22 + row * 24} width="55" height="18" rx="1" fill="#1e293b" stroke="#0f172a" strokeWidth="1"/>
            <rect x={147 + (row%2===0? 8 : -8)} y={26 + row * 24} width="12" height="10" rx="1" fill="#f8fafc" stroke="#0f172a" strokeWidth="1"/>
          </g>
        ))}
        {/* Arrow at tripped breaker */}
        <path d="M 15 74 L 68 74 M 62 69 L 68 74 L 62 79" stroke="#dc2626" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
        <text x="0" y="65" fontSize="8" fontWeight="900" fill="#dc2626" fontFamily="monospace">TRIPPED</text>
        <text x="0" y="88" fontSize="7" fontWeight="700" fill="#dc2626" fontFamily="monospace">middle position</text>
      </svg>
    </Card>
  );
}

// ── Smoke Alarm Battery ──
export function SmokeAlarmBattery() {
  return (
    <Card label="Smoke Alarm Battery" caption="Twist the smoke alarm counterclockwise to remove it from the ceiling. Open the back — a 9V battery clicks in. Ours are already stocked; we'll bring one if you don't have it.">
      <svg viewBox="0 0 220 160" width={SIZE.width} height={SIZE.height}>
        {/* Alarm disc */}
        <circle cx="110" cy="70" r="55" fill="#f8fafc" stroke="#0f172a" strokeWidth="2.5"/>
        <circle cx="110" cy="70" r="45" fill="none" stroke="#94a3b8" strokeWidth="1"/>
        {/* Test button */}
        <circle cx="110" cy="70" r="10" fill="#dc2626" stroke="#0f172a" strokeWidth="1.5"/>
        <text x="110" y="73" textAnchor="middle" fontSize="6" fontWeight="900" fill="white" fontFamily="monospace">TEST</text>
        {/* Vents */}
        {[-30,-15,0,15,30].map(a => (
          <rect key={a} x={108} y={38} width="4" height="6" fill="#94a3b8"
                transform={`rotate(${a} 110 70)`}/>
        ))}
        {/* 9V battery below */}
        <rect x="80" y="130" width="60" height="20" rx="3" fill="#facc15" stroke="#0f172a" strokeWidth="1.5"/>
        <rect x="88" y="124" width="10" height="8" rx="1" fill="#0f172a"/>
        <rect x="120" y="124" width="10" height="8" rx="1" fill="#0f172a"/>
        <text x="110" y="145" textAnchor="middle" fontSize="9" fontWeight="900" fill="#0f172a" fontFamily="monospace">9V</text>
      </svg>
    </Card>
  );
}

const REGISTRY = {
  gfci_reset: GFCIReset,
  disposal_reset: DisposalReset,
  disposal_wrench: DisposalWrench,
  shutoff_valve: ShutoffValve,
  thermostat_batteries: ThermostatBatteries,
  breaker_panel: BreakerPanel,
  smoke_alarm_battery: SmokeAlarmBattery,
};

export default function Illustration({ illustrationKey }) {
  if (!illustrationKey) return null;
  const Component = REGISTRY[illustrationKey];
  if (!Component) return null;
  return <Component/>;
}
